// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { CaretDown } from "@phosphor-icons/react";
import { Checkbox, Popover, Text } from "@radix-ui/themes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  NO_HIDDEN_SOURCES,
  REVIEW_COMMENT_TAG_IDS,
  type ReviewCommentSourceKey,
  type ReviewCommentTagId,
  reviewCommentTagLabelKey,
} from "./reviewCommentTags";

/** One "raised by" entry: Puntt's own findings, or one expert's review. */
export type ReviewSourceFilterOption = {
  key: ReviewCommentSourceKey;
  label: string;
};

type AskV2ReviewTagFilterProps = {
  activeTags: ReadonlySet<ReviewCommentTagId>;
  onToggle: (tag: ReviewCommentTagId, checked: boolean) => void;
  alwaysOn?: boolean;
  onToggleAlwaysOn?: (checked: boolean) => void;
  /** Rendered only when an expert's method raised a finding in this review. */
  sources?: readonly ReviewSourceFilterOption[];
  hiddenSources?: ReadonlySet<ReviewCommentSourceKey>;
  onToggleSource?: (source: ReviewCommentSourceKey, checked: boolean) => void;
};

/** Testid-safe suffix for a source key (`expert:<id>` -> `expert-<id>`). */
function sourceTestId(key: ReviewCommentSourceKey): string {
  return key.replace(/[^a-z0-9-]/gi, "-");
}

function filterTriggerLabel(
  activeTags: ReadonlySet<ReviewCommentTagId>,
  hiddenSourceCount: number,
  alwaysOn: boolean,
  labelFor: (tag: ReviewCommentTagId) => string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (hiddenSourceCount > 0 || !alwaysOn) {
    return t("askV2.review.tagFilterFiltered");
  }
  if (activeTags.size === REVIEW_COMMENT_TAG_IDS.length) {
    return t("askV2.review.tagFilterAll");
  }
  if (activeTags.size === 0) {
    return t("askV2.review.tagFilterNone");
  }
  if (activeTags.size === 1) {
    const only = Array.from(activeTags)[0];
    return only
      ? labelFor(only)
      : t("askV2.review.tagFilterCount", { count: 1 });
  }
  return t("askV2.review.tagFilterCount", { count: activeTags.size });
}

export function AskV2ReviewTagFilter({
  activeTags,
  onToggle,
  alwaysOn = true,
  onToggleAlwaysOn = () => undefined,
  sources = [],
  hiddenSources = NO_HIDDEN_SOURCES,
  onToggleSource = () => undefined,
}: AskV2ReviewTagFilterProps) {
  const { t } = useTranslation("ui");

  const labelFor = useMemo(
    () => (tag: ReviewCommentTagId) => t(reviewCommentTagLabelKey(tag)),
    [t],
  );

  const hiddenSourceCount = sources.filter((source) =>
    hiddenSources.has(source.key),
  ).length;
  const triggerLabel = filterTriggerLabel(
    activeTags,
    hiddenSourceCount,
    alwaysOn,
    labelFor,
    t,
  );

  return (
    <Popover.Root>
      <Popover.Trigger>
        <button
          type="button"
          className="flex items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-base-white px-2 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-2"
          data-testid="ask-v2-review-tag-filter"
          aria-label={t("askV2.review.tagFilterLabel")}
        >
          <span>{triggerLabel}</span>
          <CaretDown size={12} aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Content
        className="max-h-[min(70vh,420px)] overflow-y-auto p-2"
        data-testid="ask-v2-review-tag-filter-menu"
        aria-label={t("askV2.review.tagFilterLabel")}
      >
        <ul className="space-y-1">
          {REVIEW_COMMENT_TAG_IDS.map((tag) => {
            const checked = activeTags.has(tag);
            const label = labelFor(tag);
            return (
              <li key={tag}>
                <label
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-puntt-neutral-gray-2"
                  data-testid={`ask-v2-review-tag-filter-${tag}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => onToggle(tag, value === true)}
                  />
                  <Text size="2">{label}</Text>
                </label>
              </li>
            );
          })}
        </ul>
        <label
          className="mt-2 flex cursor-pointer items-center gap-2 rounded border-t border-puntt-neutral-gray-4 px-2 py-1.5 hover:bg-puntt-neutral-gray-2"
          data-testid="review-comment-filter-always-on"
        >
          <Checkbox
            checked={alwaysOn}
            onCheckedChange={(value) => onToggleAlwaysOn(value === true)}
            aria-label={t("askV2.review.alwaysOnChecks")}
          />
          <Text size="2">{t("askV2.review.alwaysOnChecks")}</Text>
        </label>
        {sources.length > 0 ? (
          <section
            className="mt-2 border-t border-puntt-neutral-gray-4 pt-2"
            data-testid="ask-v2-review-source-filter"
            aria-labelledby="ask-v2-review-source-filter-heading"
          >
            <Text
              as="p"
              size="1"
              id="ask-v2-review-source-filter-heading"
              className="px-2 pb-1 text-puntt-neutral-gray-10"
            >
              {t("askV2.review.sourceFilterHeading")}
            </Text>
            <ul className="space-y-1">
              {sources.map((source) => (
                <li key={source.key}>
                  <label
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-puntt-neutral-gray-2"
                    data-testid={`ask-v2-review-source-filter-${sourceTestId(source.key)}`}
                  >
                    <Checkbox
                      checked={!hiddenSources.has(source.key)}
                      onCheckedChange={(value) =>
                        onToggleSource(source.key, value === true)
                      }
                    />
                    <Text size="2">{source.label}</Text>
                  </label>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </Popover.Content>
    </Popover.Root>
  );
}
